import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl';

import type { MapProps } from '@/components/ui/map';
import { MapBox } from '@/components/ui/map';

import type { ExportPdfRef } from './types/export-pdf';

interface PrintableChartProps extends MapProps {
  className?: string;
  defaultDisplay?: string;
}

const waitForCanvasRender = (container: HTMLElement) => {
  return new Promise<void>((resolve) => {
    if (container.querySelector('canvas')) {
      resolve();
      return;
    }
    const observer = new MutationObserver(() => {
      if (container.querySelector('canvas')) {
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(container, { childList: true, subtree: true });
  });
};

export const PrintableMap = forwardRef<ExportPdfRef, PrintableChartProps>(
  ({ className, defaultDisplay = 'block', ...mapProps }, ref) => {
    const mapRef = useRef<MapRef>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [chartImage, setChartImage] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      startExportPdf: async () => {
        if (mapRef.current && containerRef.current) {
          containerRef.current.style.display = 'block';
          // Make sure Map fully rendered
          await new Promise((resolve) => setTimeout(resolve, 200));
          const map = mapRef.current.getMap();
          await waitForCanvasRender(containerRef.current);
          const canvas = map.getCanvas();
          const dataUrl = canvas.toDataURL('image/png');
          setChartImage(dataUrl);

          // Make sure Image fully rendered
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      },
      finishExportPdf: async () => {
        setChartImage(null);
        if (containerRef.current) {
          containerRef.current.style.display = defaultDisplay;
        }
      },
    }));

    if (chartImage) {
      return <img src={chartImage} alt="Chart snapshot" width="100%" className={className} />;
    }

    return (
      <div ref={containerRef} className={className} style={{ display: defaultDisplay }}>
        <MapBox ref={mapRef} {...mapProps} preserveDrawingBuffer />
      </div>
    );
  }
);
